import { useEffect, useState } from "react";
import api from "../lib/api";
import Header from "../components/Header";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Cube, ArrowsClockwise, ShieldCheck, Warning, MagnifyingGlass, X, SealCheck } from "@phosphor-icons/react";
import { HashChip, shortHash } from "../components/HashChip";
import { motion, AnimatePresence } from "framer-motion";

export default function Explorer() {
    const [data, setData] = useState({ total: 0, blocks: [] });
    const [verify, setVerify] = useState(null);
    const [loading, setLoading] = useState(true);

    // Receipt verifier state
    const [query, setQuery] = useState("");
    const [searching, setSearching] = useState(false);
    const [match, setMatch] = useState(null); // { block, anchored, election_title } | "notfound" | null

    const load = async () => {
        setLoading(true);
        try {
            const [b, v] = await Promise.all([api.get("/blockchain?limit=100"), api.get("/blockchain/verify")]);
            setData(b.data); setVerify(v.data);
        } catch {}
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const verifyReceipt = async (e) => {
        e?.preventDefault();
        const q = query.trim().toLowerCase();
        if (!q) return;
        setSearching(true);
        setMatch(null);
        try {
            const { data: r } = await api.get(`/blockchain/block/${encodeURIComponent(q)}`);
            setMatch(r);
        } catch (err) {
            if (err.response?.status === 404) setMatch("notfound");
            else setMatch("notfound");
        }
        setSearching(false);
    };

    const clearMatch = () => { setMatch(null); setQuery(""); };

    return (
        <div className="min-h-screen bg-slate-50">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
                    <div className="min-w-0">
                        <div className="overline text-civic-600 text-[10px]">Public ledger</div>
                        <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mt-0.5 text-slate-900 break-words leading-tight">Blockchain Explorer</h1>
                        <p className="text-xs text-slate-600 mt-1">Every vote becomes a hash-chained block. Genesis is at index 0.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        {verify && (
                            <Badge
                                className={`px-2.5 py-1 border text-[11px] font-semibold ${verify.valid ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}
                                data-testid="chain-status-badge"
                            >
                                {verify.valid ? <ShieldCheck size={12} weight="fill" className="mr-1" /> : <Warning size={12} weight="fill" className="mr-1" />}
                                {verify.valid ? "CHAIN VALID" : `BROKEN AT #${verify.broken_at}`}
                            </Badge>
                        )}
                        <Button variant="outline" size="sm" onClick={load} className="border-slate-300 h-8" data-testid="explorer-refresh">
                            <ArrowsClockwise size={13} className="mr-1.5" /> Refresh
                        </Button>
                    </div>
                </div>

                {/* Verify your receipt — compact */}
                <Card className="p-4 mb-4 bg-white border-slate-200" data-testid="receipt-verifier">
                    <div className="flex items-start gap-3 flex-wrap">
                        <div className="h-8 w-8 grid place-items-center rounded-md bg-civic-50 border border-civic-100 shrink-0">
                            <SealCheck size={16} weight="duotone" className="text-civic-600" />
                        </div>
                        <div className="flex-1 min-w-[260px]">
                            <div className="overline text-civic-600 text-[10px]">Verify your receipt</div>
                            <h2 className="font-heading font-bold text-sm text-slate-900 leading-tight">Paste a block hash to confirm it's anchored on chain.</h2>
                            <p className="text-[11px] text-slate-500 mt-0.5">After voting you received a SHA-256 receipt — drop it here to re-hash and prove it was sealed without alteration.</p>
                        </div>
                    </div>
                    <form onSubmit={verifyReceipt} className="mt-3 flex flex-wrap gap-2 items-center" data-testid="receipt-verify-form">
                        <div className="relative flex-1 min-w-[260px]">
                            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="e.g. 9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08"
                                className="pl-8 h-9 font-mono text-xs"
                                data-testid="receipt-hash-input"
                            />
                        </div>
                        <Button type="submit" disabled={searching || !query.trim()} className="h-9 bg-civic-600 hover:bg-civic-700 text-white text-xs" data-testid="receipt-verify-btn">
                            {searching ? "Verifying…" : "Verify hash"}
                        </Button>
                        {match && (
                            <Button type="button" variant="ghost" onClick={clearMatch} className="h-9" data-testid="receipt-clear-btn">
                                <X size={14} />
                            </Button>
                        )}
                    </form>
                    <AnimatePresence>
                        {match === "notfound" && (
                            <motion.div
                                key="notfound"
                                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                className="mt-4 p-4 rounded-md border border-red-200 bg-red-50 flex items-center gap-3"
                                data-testid="receipt-notfound"
                            >
                                <Warning size={20} weight="fill" className="text-red-600" />
                                <div className="text-sm">
                                    <div className="font-semibold text-red-800">No block with that hash</div>
                                    <div className="text-red-700">Either the hash was mistyped or the receipt does not belong to this chain.</div>
                                </div>
                            </motion.div>
                        )}
                        {match && match !== "notfound" && match.anchored && (
                            <motion.div
                                key="ok"
                                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                className="mt-4 p-4 rounded-md border border-emerald-200 bg-emerald-50"
                                data-testid="receipt-found"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <ShieldCheck size={22} weight="fill" className="text-emerald-600" />
                                    <div className="font-heading font-bold text-emerald-800">
                                        Anchored on chain at #{match.block.index}
                                        {match.election_title ? ` · ${match.election_title}` : ""}
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-3 gap-3 text-xs">
                                    <div>
                                        <div className="overline text-slate-500">Block</div>
                                        <div className="hash-mono text-slate-900 mt-1" data-testid="match-index">#{match.block.index}</div>
                                    </div>
                                    <div>
                                        <div className="overline text-slate-500">Sealed at</div>
                                        <div className="hash-mono text-slate-900 mt-1">{match.block.timestamp?.slice(0, 19).replace("T", " ")}</div>
                                    </div>
                                    <div>
                                        <div className="overline text-slate-500">Type</div>
                                        <div className="hash-mono text-slate-900 mt-1 uppercase">{match.block.data?.type}</div>
                                    </div>
                                </div>
                                <div className="mt-3 hash-mono text-[10px] text-slate-600 break-all bg-white border border-slate-200 rounded-sm p-2">
                                    {match.block.hash}
                                </div>
                            </motion.div>
                        )}
                        {match && match !== "notfound" && !match.anchored && (
                            <motion.div
                                key="tampered"
                                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                className="mt-4 p-4 rounded-md border border-red-300 bg-red-50 flex items-center gap-3"
                            >
                                <Warning size={22} weight="fill" className="text-red-700" />
                                <div className="text-sm text-red-800 font-semibold">
                                    Block found at #{match.block.index} but recomputed hash mismatch — record may be tampered.
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </Card>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="explorer-summary">
                    <Card className="px-3 py-2 bg-white border-slate-200">
                        <div className="overline text-slate-500 text-[10px]">Chain length</div>
                        <div className="font-heading font-black text-xl text-slate-900 leading-none mt-1">{data.total}</div>
                    </Card>
                    <Card className="px-3 py-2 bg-white border-slate-200">
                        <div className="overline text-slate-500 text-[10px]">Latest hash</div>
                        <div className="hash-mono text-xs text-civic-700 mt-1.5">{data.blocks[0]?.hash ? shortHash(data.blocks[0].hash) : "—"}</div>
                    </Card>
                    <Card className="px-3 py-2 bg-white border-slate-200">
                        <div className="overline text-slate-500 text-[10px]">Algorithm</div>
                        <div className="hash-mono text-xs text-slate-900 mt-1.5">SHA-256</div>
                    </Card>
                    <Card className="px-3 py-2 bg-civic-600 text-white border-civic-600">
                        <div className="overline text-civic-100 text-[10px]">Status</div>
                        <div className="font-heading font-black text-lg leading-none mt-1">{verify?.valid ? "VERIFIED" : "—"}</div>
                    </Card>
                </div>

                {loading ? (
                    <div className="text-slate-500 hash-mono text-sm">syncing chain…</div>
                ) : (
                    <div className="space-y-1.5" data-testid="blocks-list">
                        {data.blocks.map((b, i) => (
                            <motion.div
                                key={b.hash}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.25, delay: Math.min(i * 0.02, 0.4) }}
                            >
                                <BlockRow block={b} latest={i === 0} />
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function BlockRow({ block, latest }) {
    const isVote = block.data?.type === "vote";
    const isGenesis = block.data?.type === "genesis";
    return (
        <Card
            className={`px-3 py-2 border bg-white transition-colors ${latest ? "border-civic-600 ring-1 ring-civic-200" : "border-slate-200"}`}
            data-testid={`block-${block.index}`}
        >
            <div className="grid grid-cols-12 gap-3 items-center">
                <div className="col-span-12 sm:col-span-2 flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-md grid place-items-center shrink-0 ${isGenesis ? "bg-amber-50 border border-amber-200" : "bg-civic-50 border border-civic-100"}`}>
                        <Cube size={16} weight={latest ? "fill" : "duotone"} className={isGenesis ? "text-amber-600" : "text-civic-600"} />
                    </div>
                    <div>
                        <div className="overline text-slate-500 text-[9px]">Block</div>
                        <div className="hash-mono text-sm font-semibold text-slate-900 leading-tight" data-testid={`block-index-${block.index}`}>#{block.index}</div>
                    </div>
                </div>

                <div className="col-span-6 sm:col-span-2">
                    <div className="overline text-slate-500 text-[9px]">Type</div>
                    <Badge
                        className={`mt-0.5 text-[10px] py-0 px-1.5 h-4 ${isGenesis ? "bg-amber-100 text-amber-900 border-amber-300" : "bg-civic-50 text-civic-700 border-civic-200"} border`}
                        data-testid={`block-type-${block.index}`}
                    >
                        {isGenesis ? "GENESIS" : isVote ? "VOTE" : block.data?.type?.toUpperCase() || "BLOCK"}
                    </Badge>
                </div>

                <div className="col-span-12 sm:col-span-5">
                    <div className="overline text-slate-500 text-[9px]">Hash</div>
                    <div className="mt-0.5"><HashChip value={block.hash} testid={`block-hash-${block.index}`} /></div>
                </div>

                <div className="col-span-6 sm:col-span-3 sm:text-right">
                    <div className="overline text-slate-500 text-[9px]">Timestamp</div>
                    <div className="flex items-center sm:justify-end gap-1.5 mt-0.5">
                        <div className="hash-mono text-[11px] text-slate-700">{block.timestamp?.slice(0, 19).replace("T", " ")}</div>
                        {latest && (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] py-0 px-1.5 h-4">
                                <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse-soft mr-1" /> LATEST
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
        </Card>
    );
}
